import { getVariantImageUrls } from '../services/image/image-utils.js';

/**
 * Handles proactive image loading for variant combinations
 */
export function createGalleryVariantPreloader() {
  return {
    preloading_active: false,
    preloaded_combinations: new Map(),

    preloadLikelyVariants() {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) return;

      const currentFrame = this.selected_frame || this.available_frames[0];
      const currentMount = this.selected_mount || this.available_mounts[0];

      if (currentFrame && currentMount) {
        this.preloadCombination(currentFrame, currentMount, 'immediate');
      }

      this.scheduleAdjacentPreloads();
    },

    scheduleAdjacentPreloads() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.preloadAdjacentOptions(), { timeout: 3000 });
      } else {
        setTimeout(() => this.preloadAdjacentOptions(), 1000);
      }
    },

    preloadAdjacentOptions() {
      const currentFrameIndex = this.available_frames.indexOf(this.selected_frame);

      if (currentFrameIndex > 0) {
        this.preloadCombination(
          this.available_frames[currentFrameIndex - 1],
          this.selected_mount,
          'deferred'
        );
      }

      if (currentFrameIndex < this.available_frames.length - 1) {
        this.preloadCombination(
          this.available_frames[currentFrameIndex + 1],
          this.selected_mount,
          'deferred'
        );
      }
    },

    preloadCombination(frame, mount, priority = 'deferred') {
      const combinationKey = `${frame}-${mount}`;
      const status = this.preloaded_combinations.get(combinationKey);

      if (status === 'loaded' || status === 'loading') {
        return;
      }

      this.preloaded_combinations.set(combinationKey, 'queued');

      if (priority === 'immediate') {
        this.performPreload(frame, mount);
      } else if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.performPreload(frame, mount);
        }, { timeout: 5000 });
      } else {
        setTimeout(() => {
          this.performPreload(frame, mount);
        }, 500);
      }
    },

    async performPreload(frame, mount) {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) return;

      const combinationKey = `${frame}-${mount}`;
      const status = this.preloaded_combinations.get(combinationKey);

      if (status === 'loaded' || status === 'loading') {
        return;
      }

      this.preloaded_combinations.set(combinationKey, 'loading');

      const preloadUrls = new Set();

      store.selected_artworks.forEach((artwork) => {
        if (!artwork?.variants) return;

        this.available_sizes.forEach((size) => {
          const variant = store.variant_manager.findVariant(artwork, { size, frame, mount });

          if (variant?.featured_image?.src) {
            getVariantImageUrls(variant).forEach(url => preloadUrls.add(url));
          }
        });
      });

      try {
        await store.preloadImageBatch(Array.from(preloadUrls), store.orientation);
        this.preloaded_combinations.set(combinationKey, 'loaded');
      } catch (error) {
        console.error('Error preloading combination:', error);
        this.preloaded_combinations.set(combinationKey, 'failed');
      }
    },

    handleFrameHover(frame) {
      if (!this.selected_mount) return;
      this.preloadCombination(frame, this.selected_mount, 'immediate');
    },

    handleFramePointer(frame, event) {
      if (!event || event.pointerType === 'mouse') return;
      this.handleFrameHover(frame);
    },

    handleMountHover(mount) {
      if (!this.selected_frame) return;
      this.preloadCombination(this.selected_frame, mount, 'immediate');
    },

    handleMountPointer(mount, event) {
      if (!event || event.pointerType === 'mouse') return;
      this.handleMountHover(mount);
    },

    async preloadNewArtworkVariants() {
      const store = this.$store.galleryCore;
      if (!store.variant_manager || store.selected_artworks.length < 1) return;

      const newArtwork = store.selected_artworks[store.selected_artworks.length - 1];
      if (!newArtwork?.variants) return;

      const preloadUrls = new Set();

      this.preloaded_combinations.forEach((status, combinationKey) => {
        if (status === 'loaded') {
          const [frame, mount] = combinationKey.split('-');

          this.available_sizes.forEach((size) => {
            const variant = store.variant_manager.findVariant(newArtwork, { size, frame, mount });

            if (variant?.featured_image?.src) {
              getVariantImageUrls(variant).forEach(url => preloadUrls.add(url));
            }
          });
        }
      });

      if (preloadUrls.size > 0) {
        await store.preloadImageBatch(Array.from(preloadUrls), store.orientation);
      }

      if (this.selected_frame && this.selected_mount) {
        await this.performPreload(this.selected_frame, this.selected_mount);
      }
    }
  };
}
